import { useQuery, useQueries } from '@tanstack/react-query'
import { reportingApi } from './reporting'
import { queryKeys } from './query-keys'
import type { EstimatesReport } from './types'

/**
 * Fetch the aggregated estimates report for a single team. Pass null for
 * either bound to leave it open. When `teamId` is null the query stays idle.
 */
export function useEstimatesReport(
  teamId: string | number | null,
  startDate: Date | null,
  endDate: Date | null,
) {
  const startISO = startDate ? startDate.toISOString() : null
  const endISO = endDate ? endDate.toISOString() : null

  return useQuery({
    queryKey: queryKeys.reporting.estimates(teamId ?? 'none', startISO, endISO),
    queryFn: () =>
      reportingApi.estimatesReport(teamId!, {
        startDate: startISO ?? undefined,
        endDate: endISO ?? undefined,
      }),
    enabled: !!teamId,
  })
}

/**
 * Parallel fetch one report per dealership. Used by the Director performance
 * table where the group-level call only gives totals — to render rows per
 * dealership we need a per-dealership report each.
 *
 * The returned shape pairs each result back to its dealership id so callers
 * don't have to keep a parallel index in sync.
 */
export function useDealershipEstimatesReports(
  dealerships: { id: string; name: string }[],
  startDate: Date | null,
  endDate: Date | null,
  enabled = true,
) {
  const startISO = startDate ? startDate.toISOString() : null
  const endISO = endDate ? endDate.toISOString() : null

  const results = useQueries({
    queries: dealerships.map((d) => ({
      queryKey: queryKeys.reporting.estimates(d.id, startISO, endISO),
      queryFn: () =>
        reportingApi.estimatesReport(d.id, {
          startDate: startISO ?? undefined,
          endDate: endISO ?? undefined,
        }),
      enabled,
    })),
  })

  return dealerships.map((d, i) => ({
    dealership: d,
    data: results[i]?.data as EstimatesReport | undefined,
    isLoading: results[i]?.isLoading ?? false,
    error: results[i]?.error ?? null,
  }))
}
